import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../lib/api';

interface NovaMonitorSummary {
  monitorUid: string;
  name: string | null;
  policyId: number | null;
  policyName: string | null;
  hasPolicy: boolean;
  lastContactTime: string | null;
  userName: string | null;
  userSiteName: string | null;
  userSiteId: number | null;
}

interface CreateMonitorResult {
  monitorUid: string;
  monitorName: string;
  policyId: number;
  policyName: string;
  verified: boolean;
}

export interface MonitorPickerCredentials {
  novaApiUrl: string;
  username: string;
  usernameSecretRef: string;
  password: string;
  passwordSecretRef: string;
}

interface MonitorPickerProps {
  /** Saved account id when editing, or the client-generated candidate id when creating. Used to slug/name new monitors. */
  accountId: string;
  /** True once the account has a real `nova_accounts` row — switches to the saved-account routes (DB-resolved credentials) instead of the pre-save routes (credentials in the request body). */
  isSaved: boolean;
  credentials: MonitorPickerCredentials;
  value: string;
  onChange: (monitorUid: string) => void;
}

const CREATE_OPTION_VALUE = '__create_new_monitor__';

/**
 * Replaces the free-text "Monitor UID" field with a live picker sourced from
 * Nova's own admin API, plus a guided "create new monitor" action. Surfaces
 * the exact failure mode that caused the Zorggroep outage (a saved
 * `monitor_uid` that doesn't resolve to any monitor, or resolves to one with
 * no policy attached) directly in the form, instead of only showing up as a
 * `nova-poller` error hours later.
 *
 * Checks are on-demand only — no caching, no background polling. For a saved
 * account the check runs once automatically when the form opens (mirrors
 * "reproduce first" in the verification plan); for a not-yet-saved account
 * it only runs when the operator clicks "Load from Nova" (there's nothing to
 * check with yet).
 */
export const MonitorPicker: React.FC<MonitorPickerProps> = ({ accountId, isSaved, credentials, value, onChange }) => {
  const [monitors, setMonitors] = useState<NovaMonitorSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createResult, setCreateResult] = useState<CreateMonitorResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = isSaved
        ? await apiCall(`/api/config/accounts/${encodeURIComponent(accountId)}/monitor-options`)
        : await apiCall('/api/config/accounts/monitor-options/lookup', {
            method: 'POST',
            body: JSON.stringify({
              nova_api_url: credentials.novaApiUrl,
              username: credentials.username,
              username_secret_ref: credentials.usernameSecretRef,
              password: credentials.password,
              password_secret_ref: credentials.passwordSecretRef,
            }),
          });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to load monitors from Nova');
      setMonitors(data.monitors || []);
    } catch (err: any) {
      setLoadError(err.message);
      setMonitors(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, isSaved, credentials]);

  // Auto-check once when editing a saved account (there's already a
  // monitor_uid worth verifying). Not-yet-saved accounts wait for the
  // operator to fill in credentials and click "Load from Nova".
  useEffect(() => {
    if (isSaved) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved, accountId]);

  const currentEntry = monitors?.find((m) => m.monitorUid === value) ?? null;
  const checked = monitors !== null;
  const notFound = checked && !!value && !currentEntry;
  const missingPolicy = !!currentEntry && !currentEntry.hasPolicy;

  const openCreate = () => {
    setCreateName(`Abena Nova Integration - ${accountId}`);
    setCreateError(null);
    setCreateResult(null);
    setShowCreate(true);
  };

  const submitCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const endpoint = isSaved
        ? `/api/config/accounts/${encodeURIComponent(accountId)}/monitor`
        : '/api/config/accounts/monitor';
      const body: Record<string, unknown> = { name: createName };
      if (!isSaved) {
        body.account_id = accountId;
        body.nova_api_url = credentials.novaApiUrl;
        body.username = credentials.username;
        body.username_secret_ref = credentials.usernameSecretRef;
        body.password = credentials.password;
        body.password_secret_ref = credentials.passwordSecretRef;
      }
      const resp = await apiCall(endpoint, { method: 'POST', body: JSON.stringify(body) });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to create monitor');

      setCreateResult(data);
      onChange(data.monitorUid);
      setShowCreate(false);
      await load();
    } catch (err: any) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">Monitor UID</label>
      <div className="flex gap-2">
        <select
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
          value={value || ''}
          onChange={(e) => {
            if (e.target.value === CREATE_OPTION_VALUE) {
              openCreate();
              return;
            }
            onChange(e.target.value);
          }}
        >
          {!value && <option value="">-- Select a monitor --</option>}
          {value && !currentEntry && (
            <option value={value}>
              {value}
              {checked ? ' (not found in Nova)' : ''}
            </option>
          )}
          {monitors?.map((m) => (
            <option key={m.monitorUid} value={m.monitorUid}>
              {m.name || m.monitorUid}
              {m.hasPolicy ? '' : ' (no policy attached)'}
            </option>
          ))}
          <option value={CREATE_OPTION_VALUE}>+ Create new monitor...</option>
        </select>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="px-3 py-2 border rounded-lg text-xs text-gray-600 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
        >
          {loading ? 'Loading...' : checked ? 'Refresh list' : 'Load from Nova'}
        </button>
      </div>

      {loadError && <p className="mt-1 text-xs text-red-600">{loadError}</p>}

      {notFound && (
        <p className="mt-1 text-xs text-amber-600">
          "{value}" was not found among the monitors visible to this Nova login. This is the exact failure mode that
          breaks polling — use "Create new monitor" below to fix it.
        </p>
      )}
      {missingPolicy && (
        <p className="mt-1 text-xs text-amber-600">
          Monitor "{value}" exists in Nova but has no policy attached, so heartbeats will keep failing. Use "Create
          new monitor" below to fix it.
        </p>
      )}
      {!checked && !loading && !loadError && (
        <p className="mt-1 text-xs text-gray-400">
          {isSaved ? 'Checking this monitor against Nova...' : 'Fill in the Nova API URL and credentials above, then load available monitors.'}
        </p>
      )}

      {showCreate && (
        <div className="mt-2 border rounded-lg p-3 bg-gray-50 space-y-2">
          <label className="block text-xs font-medium text-gray-500 mb-1">New monitor name</label>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
          />
          <p className="text-xs text-gray-400">
            Creates (or reuses) the shared "Abena Nova Integration" policy, creates this monitor, attaches the
            policy, and verifies it with a live heartbeat call.
          </p>
          {createError && <p className="text-xs text-red-600">{createError}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void submitCreate()}
              disabled={creating || !createName.trim()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Creating & verifying...' : 'Create monitor'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {createResult && (
        <div
          className={`mt-2 text-xs px-3 py-2 rounded ${
            createResult.verified ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          Created "{createResult.monitorName}" ({createResult.monitorUid}) with policy "{createResult.policyName}".{' '}
          {createResult.verified
            ? 'Verified via a live heartbeat call.'
            : 'Heartbeat verification did not confirm a policy — double-check in Nova.'}
        </div>
      )}
    </div>
  );
};
